package com.gloopgo.game.moderation

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

data class BlockedPlayer(
    val uid: String,
    val nickname: String
)

enum class PlayerReportReason(val wireValue: String) {
    OffensiveName("offensive_name"),
    Cheating("cheating"),
    Other("other");

    companion object {
        val wireValues: Set<String> = entries.map { it.wireValue }.toSet()

        fun fromWire(value: String): PlayerReportReason? = entries.firstOrNull { it.wireValue == value }
    }
}

class IllegalPlayerReportException(message: String) : IllegalArgumentException(message)

/**
 * Client-side checks that match the `/reports/{id}` Realtime Database validation rules.
 * Returns the trimmed nickname that should be stored.
 */
fun requireValidPlayerReport(
    reporterUid: String,
    reportedUid: String,
    reportedNickname: String,
    reason: String
): String {
    if (reporterUid.isBlank() || reporterUid.length > 128) {
        throw IllegalPlayerReportException("Invalid reporter")
    }
    if (reportedUid.isBlank() || reportedUid.length > 128) {
        throw IllegalPlayerReportException("Invalid reported player")
    }
    if (reporterUid == reportedUid) {
        throw IllegalPlayerReportException("Cannot report yourself")
    }
    val nickname = reportedNickname.trim()
    if (nickname.isEmpty() || nickname.length > 32) {
        throw IllegalPlayerReportException("Invalid nickname")
    }
    if (reason !in PlayerReportReason.wireValues) {
        throw IllegalPlayerReportException("Invalid reason")
    }
    return nickname
}

fun visiblePlayerName(
    name: String,
    playerId: String,
    localPlayerId: String?,
    blockedIds: Set<String>,
    blockedLabel: String
): String {
    if (playerId.isNotEmpty() && playerId != localPlayerId && playerId in blockedIds) {
        return blockedLabel
    }
    return name
}

private const val FIELD_SEPARATOR = '\u001F'

fun encodeBlockedPlayers(players: List<BlockedPlayer>): String {
    return players.joinToString("\n") { player ->
        val nickname = player.nickname
            .replace("\n", " ")
            .replace(FIELD_SEPARATOR.toString(), "")
        "${player.uid}$FIELD_SEPARATOR$nickname"
    }
}

fun decodeBlockedPlayers(raw: String): List<BlockedPlayer> {
    if (raw.isBlank()) return emptyList()
    return raw.lineSequence().mapNotNull { line ->
        val separator = line.indexOf(FIELD_SEPARATOR)
        if (separator <= 0) return@mapNotNull null
        val uid = line.substring(0, separator).trim()
        if (uid.isEmpty()) return@mapNotNull null
        val nickname = line.substring(separator + 1).trim()
        BlockedPlayer(uid = uid, nickname = nickname)
    }.distinctBy { it.uid }.toList()
}

interface BlockedPlayersStore {
    val blocked: StateFlow<List<BlockedPlayer>>
    fun isBlocked(uid: String): Boolean
    fun block(uid: String, nickname: String)
    fun unblock(uid: String)
    fun clear()
}

class PersistedBlockedPlayersStore(
    private val load: () -> String,
    private val save: (String) -> Unit
) : BlockedPlayersStore {
    private val _blocked = MutableStateFlow(decodeBlockedPlayers(runCatching { load() }.getOrDefault("")))
    override val blocked: StateFlow<List<BlockedPlayer>> = _blocked.asStateFlow()

    override fun isBlocked(uid: String): Boolean = _blocked.value.any { it.uid == uid }

    override fun block(uid: String, nickname: String) {
        if (uid.isBlank()) return
        val next = _blocked.value.filterNot { it.uid == uid } + BlockedPlayer(uid, nickname.trim())
        persist(next)
    }

    override fun unblock(uid: String) {
        persist(_blocked.value.filterNot { it.uid == uid })
    }

    override fun clear() {
        persist(emptyList())
    }

    private fun persist(next: List<BlockedPlayer>) {
        _blocked.value = next
        runCatching { save(encodeBlockedPlayers(next)) }
    }
}

expect fun createBlockedPlayersStore(): BlockedPlayersStore
